'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { Typography } from '@/shared/ui/shadcn/typography';
import { ConceptCard } from './ConceptCard';
import { AlbumCard } from '@/features/design/history/ui/AlbumCard';
import type { HistoryItem } from '@/features/design/history';
import type { Concept } from '@/types/concept';

interface RecentConceptsCarouselProps {
  concepts: HistoryItem[] | Concept[];
  projectId: string;
  projectName?: string;
  maxConcepts?: number;
  onFavoriteToggle?: (conceptId: string, isFavorite: boolean) => void;
}

/**
 * Horizontal carousel showing recent concepts (max 5) with "View All" button
 * Used on the project page instead of the full grid
 */
export function RecentConceptsCarousel({
  concepts,
  projectId,
  projectName,
  maxConcepts = 5,
  onFavoriteToggle,
}: RecentConceptsCarouselProps) {
  const t = useTranslations('project');

  // Get recent concepts sorted by newest first
  const recentConcepts = useMemo(() => {
    return [...concepts]
      .sort((a, b) => {
        const seqA =
          (a.attributes as { project_sequence_number?: number })
            .project_sequence_number ?? 0;
        const seqB =
          (b.attributes as { project_sequence_number?: number })
            .project_sequence_number ?? 0;
        return seqB - seqA; // Newest first
      })
      .slice(0, maxConcepts);
  }, [concepts, maxConcepts]);

  if (recentConcepts.length === 0) {
    return null;
  }

  return (
    <div>
      {/* Header with "View All" button */}
      <div className="flex items-center justify-between mb-4">
        <Typography variant="h3">{t('recentConcepts')}</Typography>
        {concepts.length > maxConcepts && (
          <Link href={`/project/${projectId}/concepts`}>
            <Button variant="outline" size="sm" className="gap-1">
              {t('viewAllConcepts')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </Link>
        )}
      </div>

      {/* Horizontal scroll container */}
      <div className="relative">
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
          {recentConcepts.map((concept) => {
            const isAlbum = (concept.attributes as { is_album?: boolean })
              .is_album;
            return (
              <div
                key={concept.id}
                className="flex-shrink-0 w-[180px] sm:w-[200px] snap-start"
              >
                {isAlbum ? (
                  <AlbumCard item={concept as HistoryItem} />
                ) : (
                  <ConceptCard
                    concept={concept}
                    projectId={projectId}
                    projectName={projectName}
                    variant="compact"
                    hideActions
                    onFavoriteToggle={onFavoriteToggle}
                  />
                )}
              </div>
            );
          })}

          {/* View All card at the end if there are more concepts */}
          {concepts.length > maxConcepts && (
            <Link
              href={`/project/${projectId}/concepts`}
              className="flex-shrink-0 w-[180px] sm:w-[200px] snap-start"
            >
              <div className="aspect-[4/3] rounded-lg border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center gap-2 hover:border-primary/50 hover:bg-accent/50 transition-colors cursor-pointer">
                <div className="text-token-balance text-muted-foreground">
                  +{concepts.length - maxConcepts}
                </div>
                <Typography variant="muted" className="text-center px-2">
                  {t('viewAllConcepts')}
                </Typography>
              </div>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
